import { useEffect, useRef } from 'react';
import { io, type Socket } from 'socket.io-client';
import { authUtils } from '@shared/utils/axios-instance';
import {
  SELLER_ORDERS_EVENTS,
  SELLER_ORDERS_WS_NAMESPACE,
  SELLER_ORDERS_WS_PATH,
  type SellerNewOrderPayload,
  type SellerOrderCanceledPayload,
  type SellerOrderDostavistaPayload,
  type SellerNotificationNewPayload,
} from '../types/seller-orders-ws';

type Handlers = {
  onNewOrder?: (payload: SellerNewOrderPayload) => void;
  onOrderCanceled?: (payload: SellerOrderCanceledPayload) => void;
  onOrderDostavista?: (payload: SellerOrderDostavistaPayload) => void;
  onNotificationNew?: (payload: SellerNotificationNewPayload) => void;
};

export function useSellerOrdersSocket(handlers: Handlers, enabled = true) {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!enabled) return undefined;

    const token = authUtils.getAccessToken();
    if (!token) return undefined;

    const socket: Socket = io(SELLER_ORDERS_WS_NAMESPACE, {
      path: SELLER_ORDERS_WS_PATH,
      transports: ['websocket', 'polling'],
      auth: {
        token,
        client: 'seller-admin',
      },
    });

    socket.on('connect_error', (err) => {
      // eslint-disable-next-line no-console
      console.warn('[seller-orders-ws] connect_error', err.message);
    });
    socket.on(SELLER_ORDERS_EVENTS.orderNew, (payload: SellerNewOrderPayload) => {
      handlersRef.current.onNewOrder?.(payload);
    });
    socket.on(
      SELLER_ORDERS_EVENTS.orderCanceled,
      (payload: SellerOrderCanceledPayload) => {
        handlersRef.current.onOrderCanceled?.(payload);
      },
    );
    socket.on(
      SELLER_ORDERS_EVENTS.orderDostavista,
      (payload: SellerOrderDostavistaPayload) => {
        handlersRef.current.onOrderDostavista?.(payload);
      },
    );
    socket.on(
      SELLER_ORDERS_EVENTS.notificationNew,
      (payload: SellerNotificationNewPayload) => {
        handlersRef.current.onNotificationNew?.(payload);
      },
    );

    return () => {
      socket.disconnect();
    };
  }, [enabled]);
}
